import React from "react";
import {
  Avatar,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ActionModal,
  NoticeboardButton,
  ACTION_MODAL_FOOTER_PRIMARY_SIZE,
} from "../../shared";
import type { ChildFeeAccount, ParentChild } from "../../../types/parentFamily";
import {
  getChildFullName,
  getChildInitials,
} from "../../../utils/parentFamilyHelpers";
import { getAvatarColor } from "../../../utils/avatarUtils";
import { lightTheme } from "../../../styles/theme";
import {
  billingPatternLabel,
  categoryLabel,
  formatShortDate,
  sortFeeAccountPayments,
  summarizeFeeAccount,
} from "../../../utils/feeAccountHelpers";
import { formatLedgerCurrency, formatLedgerDate } from "../../../utils/schoolFeesHelpers";

interface FeeAccountStatementModalProps {
  open: boolean;
  onClose: () => void;
  account: ChildFeeAccount | null;
  child: ParentChild | null;
}

const SETTLED_CHIP_SX = {
  height: 22,
  color: "common.white",
  "& .MuiChip-label": {
    color: "common.white",
    px: 1,
    fontSize: "0.7rem",
  },
} as const;

export const FeeAccountStatementModal: React.FC<FeeAccountStatementModalProps> = ({
  open,
  onClose,
  account,
  child,
}) => {
  if (!account || !child) {
    return (
      <ActionModal open={open} onClose={onClose} title="Fee statement">
        <Typography variant="body2" color="text.secondary">
          Fee statement unavailable.
        </Typography>
      </ActionModal>
    );
  }

  const childName = getChildFullName(child);
  const summary = summarizeFeeAccount(account);
  const payments = sortFeeAccountPayments(account.payments);

  const detailRows: Array<[string, string]> = [
    ["Charge", account.title],
    ["Type", `${categoryLabel(account.category)} · ${billingPatternLabel(account.billingPattern)}`],
    ["Grade", child.grade || "—"],
    ["Start date", formatShortDate(account.startDate)],
  ];

  if (account.endDate) {
    detailRows.push(["End date", formatShortDate(account.endDate)]);
  }
  if (account.billingPattern === "once-off") {
    detailRows.push([
      "Amount",
      formatLedgerCurrency(account.totalAmount ?? 0, account.currency),
    ]);
    if (account.dueDate) {
      detailRows.push(["Due date", formatShortDate(account.dueDate)]);
    }
  } else {
    detailRows.push([
      "Monthly fee",
      formatLedgerCurrency(account.monthlyAmount ?? 0, account.currency),
    ]);
    detailRows.push(["Billable months", String(summary.billableMonths)]);
  }
  detailRows.push([
    "Total due",
    formatLedgerCurrency(summary.totalDue, account.currency),
  ]);
  detailRows.push([
    "Paid",
    formatLedgerCurrency(summary.totalPaid, account.currency),
  ]);

  return (
    <ActionModal
      open={open}
      onClose={onClose}
      title="Fee statement"
      maxWidth="md"
      footer={
        <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "flex-end",
            alignItems: "center",
            width: "100%",
          }}
        >
          <NoticeboardButton
            variant="primary"
            size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
            onClick={onClose}
          >
            Close
          </NoticeboardButton>
        </Box>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
          gap={1.5}
        >
          <Stack direction="row" spacing={1.25} alignItems="center" minWidth={0}>
            <Avatar
              sx={{
                width: 44,
                height: 44,
                fontSize: "0.85rem",
                fontWeight: 600,
                bgcolor: getAvatarColor(childName, lightTheme),
                color: "common.white",
              }}
            >
              {getChildInitials(child)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {childName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {account.title} · {billingPatternLabel(account.billingPattern)}
              </Typography>
            </Box>
          </Stack>
          <Chip
            size="small"
            label={summary.isSettled ? "Paid up" : summary.statusLabel}
            color={summary.isSettled ? "success" : "warning"}
            sx={SETTLED_CHIP_SX}
          />
        </Stack>

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Table size="small">
            <TableBody>
              {detailRows.map(([label, value]) => (
                <TableRow key={label}>
                  <TableCell sx={{ fontWeight: 600, width: "40%" }}>{label}</TableCell>
                  <TableCell>{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Amount paid
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Receipt No.</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length > 0 ? (
                  payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>{payment.paymentNumber}</TableCell>
                      <TableCell>{formatLedgerDate(payment.paidDate)}</TableCell>
                      <TableCell align="right">
                        {formatLedgerCurrency(payment.amountPaid, account.currency)}
                      </TableCell>
                      <TableCell>Receipt {payment.receiptNo}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        No payments recorded yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              px: 2,
              py: 1.25,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              Amount due:{" "}
              <Box component="span" sx={{ color: "primary.main" }}>
                {summary.isSettled
                  ? "Paid up"
                  : formatLedgerCurrency(summary.outstanding, account.currency)}
              </Box>
            </Typography>
          </Box>
        </Box>

        {account.lastStatementSentAt ? (
          <Typography variant="caption" color="text.secondary">
            Last sent: {formatShortDate(account.lastStatementSentAt)}
          </Typography>
        ) : null}
      </Box>
    </ActionModal>
  );
};

export default FeeAccountStatementModal;
